import { useQuery } from '@tanstack/react-query';
import { prepareUpload } from '@/lib/files';
import { supabase, unwrap, type Tables } from '@/lib/supabase';
import { tasksKey } from '../tasks/data';

export type Report = Tables<'reports'> & { report_attachments: Tables<'report_attachments'>[] };
export type Meeting = Tables<'meetings'>;
export type Attachment = Tables<'report_attachments'>;
export const BUCKET = 'report-attachments';

export const reportsKey = ['reports'] as const;
export const meetingsKey = ['meetings'] as const;

export const useReports = () =>
  useQuery({
    queryKey: reportsKey,
    queryFn: async () =>
      unwrap(
        await supabase
          .from('reports')
          .select('*, report_attachments(*)')
          .order('report_date', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(100),
      ),
  });

export const useMeetings = () =>
  useQuery({
    queryKey: meetingsKey,
    queryFn: async () =>
      unwrap(
        await supabase
          .from('meetings')
          .select('*')
          .order('meeting_date', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(100),
      ),
  });

/** Every task that came from a report or a meeting, for counts on the lists and progress in the open item. */
export const useSourceTasks = () =>
  useQuery({
    queryKey: [...tasksKey, 'sources'],
    queryFn: async () =>
      unwrap(
        await supabase
          .from('tasks')
          .select('id, title, status, owner_id, due_date, report_id, meeting_id')
          .or('report_id.not.is.null,meeting_id.not.is.null'),
      ),
  });
export type SourceTask = NonNullable<ReturnType<typeof useSourceTasks>['data']>[number];

/** One request signs every photo on screen; links last ten minutes and are refreshed before that. */
export function useSignedUrls(paths: string[]) {
  const key = [...paths].sort();
  return useQuery({
    enabled: key.length > 0,
    queryKey: ['report_urls', key],
    staleTime: 8 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(key, 600);
      if (error) throw error;
      return new Map(
        data.flatMap((d) => (d.path && d.signedUrl ? [[d.path, d.signedUrl] as const] : [])),
      );
    },
  });
}

export const isImage = (a: Pick<Attachment, 'mime_type'>) => !!a.mime_type?.startsWith('image/');

export async function uploadAttachment(reportId: string, f: File) {
  const up = await prepareUpload(f);
  const path = `${reportId}/${crypto.randomUUID()}.${up.ext}`;
  unwrap(
    await supabase.storage.from(BUCKET).upload(path, up.body, { contentType: up.contentType }),
  );
  unwrap(
    await supabase.from('report_attachments').insert({
      report_id: reportId,
      storage_path: path,
      file_name: f.name,
      mime_type: up.contentType,
    }),
  );
}

export async function removeAttachment(a: Attachment) {
  await supabase.storage.from(BUCKET).remove([a.storage_path]);
  unwrap(await supabase.from('report_attachments').delete().eq('id', a.id));
}
